/**
 * Blog content loader.
 *
 * Loads markdown articles from `pages/src/content/blog/*.md` at build time
 * via Vite `import.meta.glob` (eager + raw) so all posts ship in the JS bundle
 * (small N for now, < 30 posts target). Parses gray-matter frontmatter, renders
 * markdown body to HTML via marked, sanitizes via DOMPurify (XSS firewall).
 *
 * JSON-LD: the source markdown embeds Article + BreadcrumbList JSON in
 * ```json fenced blocks at end of file. We extract these blocks before
 * rendering so they go into <script type="application/ld+json"> rather than
 * being rendered as visible code. The "## JSON-LD Schema" heading preceding
 * those blocks is also stripped from body output.
 *
 * No silent fail: if parse fails for a post, we log structured error and
 * skip the post (loadAllPosts returns valid subset). loadPostBySlug returns
 * null when slug not found (caller renders 404).
 */

import { marked } from 'marked';
import DOMPurify from 'dompurify';

/**
 * Minimal YAML frontmatter parser.
 *
 * Supports the subset we use in blog markdown: string values (quoted or
 * unquoted), null, ISO dates (YYYY-MM-DD), integers, and YAML list of
 * strings via "- item" lines. We deliberately avoid gray-matter to save
 * ~57 KB gzipped (js-yaml pulled transitively). Any unsupported feature
 * throws so we fail loud rather than silently mis-parse.
 */
function parseFrontmatter(raw: string): { data: Record<string, unknown>; content: string } {
  if (!raw.startsWith('---')) {
    return { data: {}, content: raw };
  }
  const closeIdx = raw.indexOf('\n---', 3);
  if (closeIdx < 0) {
    throw new Error('Frontmatter: missing closing delimiter');
  }
  const block = raw.slice(3, closeIdx);
  // Skip the closing `\n---` plus trailing newline.
  let bodyStart = closeIdx + 4;
  if (raw[bodyStart] === '\n') bodyStart++;
  const content = raw.slice(bodyStart);

  const data: Record<string, unknown> = {};
  const lines = block.split('\n');
  let i = 0;
  while (i < lines.length) {
    const line = lines[i] ?? '';
    i++;
    if (!line.trim() || line.trim().startsWith('#')) continue;
    // key: value | key:
    const m = line.match(/^([a-zA-Z_][\w-]*):\s*(.*)$/);
    if (!m) continue;
    const key = m[1] as string;
    const rest = (m[2] as string).trim();
    if (rest === '' || rest === '|' || rest === '>') {
      // Multi-line: collect "- item" entries (only list syntax supported).
      const list: string[] = [];
      while (i < lines.length) {
        const nxt = lines[i] ?? '';
        const li = nxt.match(/^\s*-\s+(.*)$/);
        if (!li) break;
        list.push(stripQuotes((li[1] as string).trim()));
        i++;
      }
      data[key] = list;
      continue;
    }
    data[key] = coerceScalar(rest);
  }
  return { data, content };
}

function stripQuotes(s: string): string {
  if ((s.startsWith('"') && s.endsWith('"')) || (s.startsWith("'") && s.endsWith("'"))) {
    return s.slice(1, -1);
  }
  return s;
}

function coerceScalar(v: string): unknown {
  if (v === 'null' || v === '~') return null;
  if (v === 'true') return true;
  if (v === 'false') return false;
  if (/^-?\d+$/.test(v)) return Number(v);
  return stripQuotes(v);
}

export interface BlogPostFrontmatter {
  title: string;
  description: string;
  slug: string;
  cluster: string;
  primary_keyword?: string;
  secondary_keywords?: string[];
  author: string;
  publish_date: string;
  updated_date?: string;
  citations_count?: number;
  lang?: string;
  hreflang_en?: string | null;
  hreflang_vi?: string | null;
  og_image?: string;
}

export interface BlogPost extends BlogPostFrontmatter {
  /** Canonical slug segment (without /blog/ prefix). */
  slugSegment: string;
  /** Sanitized HTML body, ready for dangerouslySetInnerHTML. */
  bodyHtml: string;
  /** Plain-text approximation for read-time estimate. */
  plainText: string;
  /** Extracted JSON-LD objects (Article, BreadcrumbList, ...). */
  jsonLd: unknown[];
  /** Estimated read time in minutes (200 wpm). */
  readMinutes: number;
}

// Eager import all markdown files at build time as raw strings.
const RAW_MODULES = import.meta.glob<string>('../content/blog/*.md', {
  eager: true,
  query: '?raw',
  import: 'default',
});

let cache: BlogPost[] | null = null;

function deriveSlugSegment(_frontmatterSlug: string, filePath: string): string {
  // Filename la source of truth cho route segment. Frontmatter `slug` field
  // (vd "/en/expat/foreigner-buy-property-vietnam") luu i18n/canonical intent,
  // NHUNG SPA route la `/blog/:slug` flat - chi match basename. SSG path
  // generation phai match route shape -> luon dung filename.
  //
  // Bug history 2026-05-19: EN article voi frontmatter slug "/en/expat/<name>"
  // tao path `dist/blog/en/expat/<name>/index.html`, KHONG khop route SPA ->
  // 6 URL `/blog/<name>` rot ve homepage SPA fallback (21KB duplicate HTML).
  // Fix: ignore frontmatterSlug, luon dung basename.
  const base = filePath.split('/').pop() ?? '';
  return base.replace(/\.md$/, '');
}

function extractJsonLdBlocks(markdown: string): { body: string; jsonLd: unknown[] } {
  const jsonLd: unknown[] = [];
  // Strip the "## JSON-LD Schema" heading and any subsequent json blocks
  // (both ```json fenced + raw <script type="application/ld+json"> form).
  let body = markdown;
  const headingIdx = body.search(/##\s+JSON-LD\s+Schema/i);
  let tail = '';
  if (headingIdx >= 0) {
    tail = body.slice(headingIdx);
    body = body.slice(0, headingIdx);
  }

  function parseAndPush(raw: string): void {
    const trimmed = raw.trim();
    if (!trimmed) return;
    try {
      jsonLd.push(JSON.parse(trimmed));
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(
        JSON.stringify({
          level: 'error',
          scope: 'blog-loader',
          event: 'json_ld_parse_fail',
          message: (err as Error).message,
        }),
      );
    }
  }

  const fencedRe = /```json\s*\n([\s\S]*?)```/g;
  // Raw <script type="application/ld+json">...</script> block embedded thuc
  // tiep trong markdown (article style hien tai, vd cap-duong-nuoi-con.md).
  const scriptRe = /<script\s+type=["']application\/ld\+json["']\s*>([\s\S]*?)<\/script>/gi;

  for (const src of [tail, body]) {
    let m: RegExpExecArray | null;
    while ((m = fencedRe.exec(src)) !== null) parseAndPush(m[1] ?? '');
    fencedRe.lastIndex = 0;
    while ((m = scriptRe.exec(src)) !== null) parseAndPush(m[1] ?? '');
    scriptRe.lastIndex = 0;
  }

  // Strip extracted blocks tu body de KHONG render visible code/leaked tag.
  body = body.replace(fencedRe, '');
  body = body.replace(scriptRe, '');
  // Drop separator "---" that previously separated body from JSON-LD section.
  body = body.replace(/\n---\s*$/m, '\n');
  return { body, jsonLd };
}

function renderMarkdown(body: string): string {
  marked.setOptions({ gfm: true, breaks: false });
  const rawHtml = marked.parse(body, { async: false }) as string;
  if (typeof window === 'undefined') {
    // SSR / preview build pre-pass: DOMPurify needs window. Return raw, will
    // sanitize on hydrate. For Cloudflare Pages SPA we always run in browser.
    return rawHtml;
  }
  return DOMPurify.sanitize(rawHtml, {
    USE_PROFILES: { html: true },
    ADD_ATTR: ['target', 'rel'],
  });
}

function plainText(html: string): string {
  return html
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function readMinutes(text: string): number {
  const words = text.split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.round(words / 200));
}

function parsePost(filePath: string, raw: string): BlogPost | null {
  try {
    const parsed = parseFrontmatter(raw);
    const fm = parsed.data as Partial<BlogPostFrontmatter> & Record<string, unknown>;
    // Frontmatter schema tolerance: some EN article cu dung `category` thay
    // `cluster`, `last_updated` thay `updated_date`. Map alias truoc khi
    // validate de tranh parse fail (4 article EN ship 2026-05-19: fdi-company
    // -vietnam-setup, pit-vietnam-foreigners, thanh-lap-cong-ty-tnhh,
    // vietnam-work-permit). Canonical field van la cluster + updated_date.
    if (!fm.cluster && typeof fm.category === 'string') {
      fm.cluster = fm.category;
    }
    if (!fm.updated_date && typeof fm.last_updated === 'string') {
      fm.updated_date = fm.last_updated;
    }
    if (!fm.title || !fm.description || !fm.publish_date || !fm.cluster || !fm.author) {
      // eslint-disable-next-line no-console
      console.error(
        JSON.stringify({
          level: 'error',
          scope: 'blog-loader',
          event: 'missing_required_frontmatter',
          file: filePath,
          have: Object.keys(fm),
        }),
      );
      return null;
    }
    const slugSegment = deriveSlugSegment(fm.slug ?? '', filePath);
    const { body, jsonLd } = extractJsonLdBlocks(parsed.content);
    const bodyHtml = renderMarkdown(body);
    const text = plainText(bodyHtml);
    return {
      title: fm.title,
      description: fm.description,
      slug: fm.slug ?? `/blog/${slugSegment}`,
      cluster: fm.cluster,
      primary_keyword: fm.primary_keyword,
      secondary_keywords: fm.secondary_keywords,
      author: fm.author,
      publish_date: fm.publish_date,
      updated_date: fm.updated_date ?? fm.publish_date,
      citations_count: fm.citations_count,
      lang: fm.lang ?? 'vi',
      hreflang_en: fm.hreflang_en ?? null,
      hreflang_vi: fm.hreflang_vi ?? null,
      og_image: fm.og_image,
      slugSegment,
      bodyHtml,
      plainText: text,
      jsonLd,
      readMinutes: readMinutes(text),
    };
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error(
      JSON.stringify({
        level: 'error',
        scope: 'blog-loader',
        event: 'parse_fail',
        file: filePath,
        message: (err as Error).message,
      }),
    );
    return null;
  }
}

function loadAllSync(): BlogPost[] {
  if (cache) return cache;
  const posts: BlogPost[] = [];
  for (const [path, raw] of Object.entries(RAW_MODULES)) {
    const post = parsePost(path, raw);
    if (post) posts.push(post);
  }
  posts.sort((a, b) => (a.publish_date < b.publish_date ? 1 : -1));
  cache = posts;
  return posts;
}

export async function loadAllPosts(): Promise<BlogPost[]> {
  return loadAllSync();
}

export async function loadPostBySlug(slug: string): Promise<BlogPost | null> {
  const all = loadAllSync();
  const normalized = slug.replace(/^\/+/, '').replace(/^blog\//, '');
  return all.find((p) => p.slugSegment === normalized) ?? null;
}

/**
 * Sync variant cua loadAllPosts. Vi RAW_MODULES la `eager: true` o trong
 * import.meta.glob, tat ca .md raw text da o trong bundle - sync resolve
 * OK. Dung trong SSG render pass (no async lifecycle) + initial client
 * render de SEO-friendly first paint.
 */
export function loadAllPostsSync(): BlogPost[] {
  return loadAllSync();
}

/**
 * Sync variant cua loadPostBySlug. Cung pattern voi loadAllPostsSync.
 */
export function loadPostBySlugSync(slug: string): BlogPost | null {
  const all = loadAllSync();
  const normalized = slug.replace(/^\/+/, '').replace(/^blog\//, '');
  return all.find((p) => p.slugSegment === normalized) ?? null;
}

export const CLUSTER_LABEL_VI: Record<string, string> = {
  'lao-dong': 'Lao động và việc làm',
  'dat-dai': 'Đất đai và nhà ở',
  'hon-nhan': 'Hôn nhân và gia đình',
  'doanh-nghiep': 'Doanh nghiệp',
  'expat': 'Người nước ngoài và đầu tư',
  'expat-en': 'For Expats (English)',
  've-asklaw': 'Về Asklaw',
  've-asklaw-en': 'About Asklaw (English)',
};

export const CLUSTER_LABEL_EN: Record<string, string> = {
  'lao-dong': 'Labor & Employment',
  'dat-dai': 'Land & Housing',
  'hon-nhan': 'Marriage & Family',
  'doanh-nghiep': 'Business',
  'expat': 'Expat & Investment',
  'expat-en': 'For Expats (English)',
  've-asklaw': 'About Asklaw',
  've-asklaw-en': 'About Asklaw',
};

export function clusterLabel(cluster: string): string {
  return CLUSTER_LABEL_VI[cluster] ?? cluster;
}

const SITE_ORIGIN = 'https://asklaw.vn';

/**
 * Normalize a frontmatter hreflang reference into an absolute, canonical blog
 * URL of the served route shape `https://asklaw.vn/blog/<slug>/` (flat, trailing
 * slash).
 *
 * Frontmatter hreflang_en / hreflang_vi values are historically a *path* that
 * may carry a legacy nested prefix (vd `/en/ve-asklaw/<name>` or
 * `/blog/<name>` without trailing slash). The actual served URL ignores the
 * nested prefix and uses the filename basename only (see deriveSlugSegment +
 * SSG route shape s2026-05-20). So we extract the last path segment and rebuild
 * the canonical flat URL. Accepts a bare slug, a path, or a full URL.
 *
 * Returns null for empty / null input.
 */
export function normalizeHreflangUrl(ref: string | null | undefined): string | null {
  if (!ref) return null;
  let s = ref.trim();
  if (!s) return null;
  // Strip protocol + host if a full URL was provided.
  s = s.replace(/^https?:\/\/[^/]+/i, '');
  // Strip query / hash.
  s = s.replace(/[?#].*$/, '');
  // Take the last non-empty path segment as the slug (drops /en/<scope>/ or
  // /blog/ prefixes). Trailing slash tolerated.
  const segs = s.split('/').filter(Boolean);
  const slug = segs.length > 0 ? segs[segs.length - 1] : '';
  if (!slug) return null;
  return `${SITE_ORIGIN}/blog/${slug}/`;
}

export interface HreflangLink {
  hreflang: string;
  href: string;
}

/**
 * Build the rel=alternate hreflang link set for a blog post.
 *
 * Returns an empty array when the post has no cross-language counterpart
 * (no hreflang_en and no hreflang_vi) - a single-language page does not need
 * alternate links. When a pair exists, returns a self-referencing set of:
 *   - hreflang="vi"        -> the Vietnamese version
 *   - hreflang="en"        -> the English version
 *   - hreflang="x-default" -> the Vietnamese version (VN-first product)
 *
 * The "self" side is the post's own canonical URL; the "other" side is the
 * normalized counterpart from frontmatter. x-default always points at the VI
 * URL of the pair regardless of which language page is being rendered.
 */
export function buildHreflangLinks(post: {
  slugSegment: string;
  lang?: string;
  hreflang_en?: string | null;
  hreflang_vi?: string | null;
}): HreflangLink[] {
  const selfUrl = `${SITE_ORIGIN}/blog/${post.slugSegment}/`;
  const isEn = post.lang === 'en';

  let viUrl: string | null;
  let enUrl: string | null;
  if (isEn) {
    enUrl = selfUrl;
    viUrl = normalizeHreflangUrl(post.hreflang_vi);
  } else {
    viUrl = selfUrl;
    enUrl = normalizeHreflangUrl(post.hreflang_en);
  }

  // No counterpart -> no alternate set (single-language page).
  if (!viUrl || !enUrl) return [];

  return [
    { hreflang: 'vi', href: viUrl },
    { hreflang: 'en', href: enUrl },
    // VN-first: the default version for unmatched locales is Vietnamese.
    { hreflang: 'x-default', href: viUrl },
  ];
}
